import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';

export const useMonthlyInvoiceReports = () => {
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token || localStorage.getItem('authToken')}` }), [token]);
  const currentYear = new Date().getFullYear();

  const [activeTab, setActiveTab] = useState('overview');
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [trendData, setTrendData] = useState(null);
  const [trendYear, setTrendYear] = useState(currentYear);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState('');
  const [ownerData, setOwnerData] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerError, setOwnerError] = useState('');
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(0);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [draftFilters, setDraftFilters] = useState({ owner_code: '', status: '', search: '', month: '' });
  const [appliedFilters, setAppliedFilters] = useState({ owner_code: '', status: '', search: '', month: '' });
  const summaryLoadedYearRef = useRef(null);
  const ownersLoadedYearRef = useRef(null);
  const ownerOptionsLoadedRef = useRef(false);
  const pageSize = 25;

  const reportYearOptions = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const yearValue = currentYear - 3 + index;
      return { value: yearValue, label: String(yearValue) };
    })
  ), [currentYear]);

  const reportDateFilters = useMemo(() => ({
    date_from: `${trendYear}-01-01`,
    date_to: `${trendYear}-12-31`
  }), [trendYear]);

  const invoiceDateFilters = useMemo(() => {
    const monthValue = Number(appliedFilters.month || 0);

    if (!monthValue) {
      return {
        date_from: `${trendYear}-01-01`,
        date_to: `${trendYear}-12-31`
      };
    }

    const startMonth = String(monthValue).padStart(2, '0');
    const lastDay = new Date(trendYear, monthValue, 0).getDate();

    return {
      date_from: `${trendYear}-${startMonth}-01`,
      date_to: `${trendYear}-${startMonth}-${String(lastDay).padStart(2, '0')}`
    };
  }, [appliedFilters.month, trendYear]);

  const invoiceDateRangeLabel = useMemo(() => {
    const monthValue = Number(appliedFilters.month || 0);
    if (!monthValue) {
      return `${trendYear}`;
    }

    const monthLabel = new Date(2000, monthValue - 1, 1).toLocaleString('default', { month: 'long' });
    return `${monthLabel} ${trendYear}`;
  }, [appliedFilters.month, trendYear]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const response = await axios.get(
        `${rootUrl}/api/owners/transactions/reports/monthly/summary?${new URLSearchParams(reportDateFilters).toString()}`,
        { headers: authHeaders }
      );
      setSummaryData(response.data?.data || null);
      summaryLoadedYearRef.current = trendYear;
    } catch (error) {
      setSummaryError(error.response?.data?.message || 'Failed to load monthly invoice summary.');
    } finally {
      setSummaryLoading(false);
    }
  }, [authHeaders, reportDateFilters, trendYear]);

  const fetchOwners = useCallback(async () => {
    setOwnerLoading(true);
    setOwnerError('');
    try {
      const params = new URLSearchParams({ limit: '50', ...reportDateFilters });
      const response = await axios.get(`${rootUrl}/api/owners/transactions/reports/monthly/owners?${params.toString()}`, { headers: authHeaders });
      setOwnerData(Array.isArray(response.data?.data) ? response.data.data : []);
      ownersLoadedYearRef.current = trendYear;
    } catch (error) {
      setOwnerError(error.response?.data?.message || 'Failed to load owner totals.');
      setOwnerData([]);
    } finally {
      setOwnerLoading(false);
    }
  }, [authHeaders, reportDateFilters, trendYear]);

  const fetchTrend = useCallback(async () => {
    setTrendLoading(true);
    setTrendError('');
    try {
      const response = await axios.get(`${rootUrl}/api/owners/transactions/reports/monthly/trend?year=${trendYear}`, { headers: authHeaders });
      setTrendData(response.data?.data || null);
    } catch (error) {
      setTrendError(error.response?.data?.message || 'Failed to load monthly trend report.');
      setTrendData(null);
    } finally {
      setTrendLoading(false);
    }
  }, [authHeaders, trendYear]);

  const fetchOwnerOptions = useCallback(async () => {
    if (ownerOptionsLoadedRef.current) return;
    try {
      const response = await axios.get(`${rootUrl}/owners/list`, { headers: authHeaders });
      const rows = Array.isArray(response.data) ? response.data : Array.isArray(response.data?.data) ? response.data.data : [];
      setOwnerOptions(rows.filter((owner) => owner?.code).map((owner) => ({ value: owner.code, label: `${owner.code} - ${owner.name}` })));
      ownerOptionsLoadedRef.current = true;
    } catch (error) {
      setOwnerOptions([]);
      console.error('Failed to load owner filter options:', error);
    }
  }, [authHeaders]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const params = new URLSearchParams({ limit: String(pageSize), offset: String(invoicePage * pageSize), ...invoiceDateFilters });
      if (appliedFilters.owner_code) params.set('owner_code', appliedFilters.owner_code);
      if (appliedFilters.status) params.set('status', appliedFilters.status);
      if (appliedFilters.search) params.set('search', appliedFilters.search.trim());
      const response = await axios.get(`${rootUrl}/api/owners/transactions/reports/monthly/invoices?${params.toString()}`, { headers: authHeaders });
      setInvoiceRows(Array.isArray(response.data?.data) ? response.data.data : []);
      setInvoiceTotal(Number(response.data?.total || 0));
    } catch (error) {
      setInvoiceError(error.response?.data?.message || 'Failed to load monthly invoice list.');
      setInvoiceRows([]);
      setInvoiceTotal(0);
    } finally {
      setInvoiceLoading(false);
    }
  }, [appliedFilters.month, appliedFilters.owner_code, appliedFilters.search, appliedFilters.status, authHeaders, invoiceDateFilters, invoicePage]);

  useEffect(() => {
    if ((activeTab === 'overview' || activeTab === 'status') && summaryLoadedYearRef.current !== trendYear) {
      fetchSummary();
    }
  }, [activeTab, fetchSummary, trendYear]);

  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchTrend();
    }
  }, [activeTab, fetchTrend]);

  useEffect(() => {
    if (activeTab === 'owners' && ownersLoadedYearRef.current !== trendYear) {
      fetchOwners();
    }
  }, [activeTab, fetchOwners, trendYear]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchOwnerOptions();
    }
  }, [activeTab, fetchOwnerOptions]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    }
  }, [activeTab, fetchInvoices]);

  useEffect(() => {
    setInvoicePage(0);
  }, [trendYear, appliedFilters.month]);

  const summary = summaryData?.summary || {};
  const statusRows = summaryData?.statuses || [];
  const statusMonthRows = Array.isArray(summaryData?.status_months) ? summaryData.status_months : [];
  const statusAmountMonthRows = Array.isArray(summaryData?.status_amount_months) ? summaryData.status_amount_months : [];
  const paymentRows = summaryData?.payments || [];
  const ownerRows = ownerData || [];
  const trendSummary = trendData?.summary || {};
  const trendMonthRows = Array.isArray(trendData?.months) ? trendData.months : [];
  const trendOwnerRows = Array.isArray(trendData?.owners) ? trendData.owners : [];
  const trendOwnerMonthRows = Array.isArray(trendData?.owner_months) ? trendData.owner_months : [];

  const statusChart = useMemo(() => statusMonthRows.map((row) => ({
    month_no: Number(row.month_no || 0),
    month_label: row.month_label || '-',
    DRAFT: Number(row.draft_count || 0),
    SENT: Number(row.sent_count || 0),
    PAID: Number(row.paid_count || 0),
    total: Number(row.draft_count || 0) + Number(row.sent_count || 0) + Number(row.paid_count || 0)
  })), [statusMonthRows]);
  const amountChart = useMemo(() => statusAmountMonthRows.map((row) => ({
    month_no: Number(row.month_no || 0),
    month_label: row.month_label || '-',
    DRAFT: Number(row.draft_amount || 0),
    SENT: Number(row.sent_amount || 0),
    PAID: Number(row.paid_amount || 0),
    total: Number(row.draft_amount || 0) + Number(row.sent_amount || 0) + Number(row.paid_amount || 0)
  })), [statusAmountMonthRows]);
  const paymentChart = useMemo(() => paymentRows.map((row) => ({ name: row.bucket === 'PAID' ? 'Paid' : 'Not paid', bucket: row.bucket, count: Number(row.count || 0) })), [paymentRows]);
  const ownerChart = useMemo(() => ownerRows.slice(0, 10).map((row) => ({ name: row.owner_code, net: Number(row.net_amount || 0) })), [ownerRows]);
  const trendChart = useMemo(() => trendMonthRows.map((row) => ({
    month_no: Number(row.month_no || 0),
    month_label: row.month_label || '-',
    invoice_count: Number(row.invoice_count || 0),
    gross_amount: Number(row.gross_amount || 0),
    discount_amount: Number(row.discount_amount || 0),
    net_amount: Number(row.net_amount || 0)
  })), [trendMonthRows]);
  const trendTopOwners = useMemo(() => trendOwnerRows.slice(0, 8).map((row) => ({
    owner_code: row.owner_code,
    owner_name: row.owner_name,
    count: Number(row.count || 0),
    gross_amount: Number(row.gross_amount || 0),
    discount_amount: Number(row.discount_amount || 0),
    net_amount: Number(row.net_amount || 0)
  })), [trendOwnerRows]);
  const trendOwnerMatrix = useMemo(() => {
    const ownerMap = trendOwnerRows.slice(0, 8).reduce((acc, row) => {
      acc[row.owner_code] = {
        owner_code: row.owner_code,
        owner_name: row.owner_name,
        months: {},
        total: Number(row.net_amount || 0)
      };
      return acc;
    }, {});

    trendOwnerMonthRows.forEach((row) => {
      const ownerCode = row.owner_code;
      if (!ownerMap[ownerCode]) {
        return;
      }

      ownerMap[ownerCode].months[Number(row.month_no || 0)] = Number(row.net_amount || 0);
    });

    return Object.values(ownerMap).map((owner) => ({
      ...owner,
      months: Object.fromEntries(
        Array.from({ length: 12 }, (_, index) => {
          const monthNo = index + 1;
          return [monthNo, owner.months[monthNo] || 0];
        })
      )
    }));
  }, [trendOwnerMonthRows, trendOwnerRows]);

  const invoiceStart = invoiceTotal === 0 ? 0 : invoicePage * pageSize + 1;
  const invoiceEnd = Math.min(invoiceTotal, (invoicePage + 1) * pageSize);
  const invoicePages = Math.max(1, Math.ceil(invoiceTotal / pageSize));

  const applyFilters = useCallback(() => {
    setInvoicePage(0);
    setAppliedFilters({ ...draftFilters });
  }, [draftFilters]);

  const resetFilters = useCallback(() => {
    const empty = { owner_code: '', status: '', search: '', month: '' };
    setDraftFilters(empty);
    setAppliedFilters(empty);
    setInvoicePage(0);
  }, []);

  return {
    activeTab,
    setActiveTab,
    currentYear,
    trendYear,
    setTrendYear,
    reportYearOptions,
    summaryLoading,
    summaryError,
    summary,
    statusRows,
    paymentRows,
    statusChart,
    amountChart,
    paymentChart,
    trendLoading,
    trendError,
    trendSummary,
    trendChart,
    trendTopOwners,
    trendOwnerMatrix,
    trendMonthRows,
    ownerLoading,
    ownerError,
    ownerRows,
    ownerChart,
    invoiceLoading,
    invoiceError,
    invoiceRows,
    invoiceTotal,
    invoicePage,
    setInvoicePage,
    invoicePages,
    invoiceStart,
    invoiceEnd,
    invoiceDateRangeLabel,
    draftFilters,
    setDraftFilters,
    ownerOptions,
    applyFilters,
    resetFilters,
  };
};

export default useMonthlyInvoiceReports;
